// src/packages/care/prediction/evidenceMapper.ts
//
// Maps the app's existing calendar data into the normalized evidence model. Today this
// covers period starts; specialist mappers (LH, BBT, mucus, wearable) will land here once
// their backend schema exists (issue #1953).
//
// Reuses extractPeriodBlocks from calendar.types (the same consecutive-day block detection
// the calendar already uses) rather than re-implementing it — one block detector, no drift.

import { parseLocalDate, getToday } from "@src/packages/utils/date";
import { extractPeriodBlocks, type CalendarItem } from "../calendar.types";
import type { LocalDate } from "./fertileWindow.types";
import type { Observation, PatientEvidence } from "./evidence.types";

/**
 * Whole years from a date-of-birth string, or null if absent/implausible.
 * Used to age-adjust the cycle-length prior; mirrors the clinics predictor's age handling.
 */
export function ageYearsFromDob(
  dateOfBirth: string | null | undefined,
): number | null {
  if (!dateOfBirth) return null;
  const dob = parseLocalDate(dateOfBirth);
  const now = getToday();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  return age >= 0 && age < 120 ? age : null;
}

export type PeriodEvidenceInput = {
  /**
   * Calendar items containing logged + medical-history-derived period days.
   * Predicted items may be present — they are filtered out here so model output never
   * feeds back in as observed evidence.
   */
  items: CalendarItem[];
  /** Patient age in years, if known. */
  ageYears?: number | null;
  /** Patient-declared cycle-length bounds from menstrual-health history. */
  declaredCycleLengthDays?: { min: number | null; max: number | null } | null;
};

/**
 * Build the patient's evidence set from calendar items.
 * Each observed period BLOCK contributes one `period_start` observation (its first day).
 */
export function mapPeriodEvidence(input: PeriodEvidenceInput): PatientEvidence {
  // Exclude predicted/derived period items before any statistics are derived.
  const observedPeriodItems = input.items.filter(
    (item) => item.type === "period" && item.meta?.predicted !== true,
  );

  const blocks = extractPeriodBlocks(observedPeriodItems);

  const periodStarts: Observation[] = blocks.map((block) => ({
    type: "period_start",
    date: block[0]!,
    value: null,
    unit: null,
    // Period logs are patient/provider-entered; the distinction doesn't affect cycle
    // statistics, so provenance is collapsed to "patient" for the period-start channel.
    source: "patient",
    measured: true,
  }));

  return {
    observations: periodStarts,
    ageYears: input.ageYears ?? null,
    declaredCycleLengthDays: input.declaredCycleLengthDays ?? null,
  };
}

/**
 * Selector: observed period-start dates, ascending and de-duplicated.
 * Enforces `measured` so derived values can never enter cycle-length calculations.
 */
export function getPeriodStartDates(evidence: PatientEvidence): LocalDate[] {
  const dates = evidence.observations
    .filter((o) => o.type === "period_start" && o.measured)
    .map((o) => o.date);
  return [...new Set(dates)].sort((a, b) => a.localeCompare(b));
}
